import type { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import axios from 'axios';
import cookies from 'js-cookie';
import { CognitoErrorCodesEnum } from '../types/eunms';
import type { Tokens } from './types';
import type { PlatformsEnum, ResponseResult } from '@utils';
import { NetworkClientBuilder, REFRESH_TOKEN_HEADER, USERNAME_HEADER, BOND_USER_ID_TOKEN_COOKIE } from '@utils';

export function mapAxiosErrorToErrorResponse(error: AxiosError<any>): AxiosResponse<any> {
	const err: string = error.response?.data?.err;
	const response: AxiosResponse<any, any> | undefined = error.response;

	if (response) {
		response.data = {
			err: err ?? response.data?.message,
			status: response.status,
			rawErr: response.data,
		};

		return response;
	} else {
		throw error;
	}
}

export function handleSuccessfulResponse(response: AxiosResponse<any>): AxiosResponse<ResponseResult<any>> {
	response.data = {
		result: response.data,
	};

	return response;
}

export function handleErrorResponse(errors: {
	generic: string;
}): (error: AxiosError<any>) => Promise<Partial<AxiosResponse<ResponseResult<any>>>> {
	return (error: AxiosError<any>) => {
		const response = error?.response;
		const data = error.response?.data;

		if (!response) {
			return Promise.resolve({
				data: { error: { message: errors.generic, code: CognitoErrorCodesEnum.GENERIC_ERROR } },
			});
		}

		if (data && data.code === CognitoErrorCodesEnum.GENERIC_ERROR) {
			response.data = {
				error: {
					message: errors.generic,
					code: data.code,
				},
			};
			return Promise.resolve(response);
		}

		return Promise.resolve(response);
	};
}

export function isSSR() {
	return typeof window === 'undefined';
}

/**
 * Detects if the current window is running inside an iframe
 */
export function isIframe(): boolean {
	if (isSSR()) {
		return false;
	}

	try {
		return window.self !== window.top;
	} catch (e) {
		// If we can't access window.top, we're likely in an iframe with cross-origin restrictions
		return true;
	}
}

const TOKEN_MAP: Record<keyof Tokens, string> = {
	accessToken: 'AccessToken',
	refreshToken: 'RefreshToken',
	userIdToken: 'BondUserIdToken',
	username: 'Username',
};

const TOKEN_KEYS = Object.keys(TOKEN_MAP) as (keyof Tokens)[];
const REQUIRED_TOKEN_KEYS: (keyof Tokens)[] = ['accessToken', 'refreshToken', 'username'];
const CREDENTIAL_SESSION_FAILED_MESSAGE = 'Unable to complete sign in. Please try again.';

export function setTokens(tokens: Partial<Tokens>): void {
	if (isSSR()) {
		return;
	}

	for (const key of TOKEN_KEYS) {
		const token = tokens[key];
		if (!token) {
			continue;
		}

		localStorage.setItem(TOKEN_MAP[key], token);
	}
}

function isNonEmptyString(value: unknown): value is string {
	return typeof value === 'string' && value.length > 0;
}

function isTokenPayload(value: unknown): value is Partial<Tokens> {
	if (typeof value !== 'object' || value === null || Array.isArray(value)) {
		return false;
	}

	const record = value as Record<string, unknown>;

	if (!REQUIRED_TOKEN_KEYS.every(key => isNonEmptyString(record[key]))) {
		return false;
	}

	const userIdToken = record.userIdToken;
	return userIdToken === undefined || isNonEmptyString(userIdToken);
}

/**
 * Persists credentials from a login/signup success payload when present.
 * Returns true when tokens were written.
 */
export function applyCredentialsFromResult(result: unknown): boolean {
	if (typeof result !== 'object' || result === null || !('credentials' in result)) {
		return false;
	}

	const credentials = (result as { credentials: unknown }).credentials;
	if (!isTokenPayload(credentials)) {
		return false;
	}

	setTokens(credentials);
	return true;
}

/**
 * Writes credentials from a success payload, or returns a generic error the form can show.
 */
export function persistCredentialSession(result: unknown): ResponseResult<unknown> | undefined {
	if (applyCredentialsFromResult(result)) {
		return undefined;
	}

	return {
		error: {
			code: CognitoErrorCodesEnum.GENERIC_ERROR,
			message: CREDENTIAL_SESSION_FAILED_MESSAGE,
		},
	};
}

export function getTokens(): Partial<Tokens> {
	if (isSSR()) {
		return {};
	}

	const accessToken: string = localStorage.getItem(TOKEN_MAP.accessToken)!;
	const refreshToken: string = localStorage.getItem(TOKEN_MAP.refreshToken)!;
	// using cookies as fallback
	const userIdToken: string | undefined =
		localStorage.getItem(TOKEN_MAP.userIdToken)! ||
		(NetworkClientBuilder.includeCredentials ? cookies.get(BOND_USER_ID_TOKEN_COOKIE)! : undefined);
	const username: string = localStorage.getItem(TOKEN_MAP.username)!;

	return { accessToken, refreshToken, userIdToken, username };
}

function credentialRequestHeaders(
	tokens: Partial<Tokens>,
	extra?: Record<string, string>
): Record<string, string> {
	return {
		...extra,
		...(isNonEmptyString(tokens.refreshToken) ? { [REFRESH_TOKEN_HEADER]: tokens.refreshToken } : {}),
		...(isNonEmptyString(tokens.username) ? { [USERNAME_HEADER]: tokens.username } : {}),
	};
}

export function clearTokens(): void {
	if (isSSR()) {
		return;
	}

	localStorage.removeItem(TOKEN_MAP.accessToken);
	localStorage.removeItem(TOKEN_MAP.refreshToken);
	localStorage.removeItem(TOKEN_MAP.userIdToken);
	localStorage.removeItem(TOKEN_MAP.username);
}

export async function refreshTokens(
	refreshUrl: string,
	platform: PlatformsEnum,
	debugHeaders?: Record<string, string>
): Promise<AxiosResponse> {
	const tokens = getTokens();

	const response = await axios.get(`${refreshUrl}?platform=${platform}`, {
		headers: credentialRequestHeaders(tokens, debugHeaders),
		withCredentials: true,
	});

	setTokens(response.data);

	return response;
}

export async function logout(
	logout: string,
	logoutRedirectUrl: string,
	onLogout?: (data: any) => void,
	reason?: string
): Promise<AxiosResponse<any>> {
	const tokens = getTokens();

	clearTokens();

	let error: Error;

	return await axios
		.delete(logout, {
			params: {
				redirectUri: logoutRedirectUrl,
				...(reason && { reason }),
			},
			headers: credentialRequestHeaders(tokens),
			withCredentials: true,
		})
		.catch(err => (error = err))
		.finally(() => onLogout?.(error));
}

/**
 * Create headers for debugging
 * @param config
 */
export function getDebugHeaders(config: InternalAxiosRequestConfig): Record<string, string> {
	let organizationId: string | null = null;

	if (config.url) {
		const url = new URL(config.url);
		organizationId = url.searchParams.get('organizationId');
	}

	return {
		...(config.url && { 'x-debug-request-url': config.url }),
		...(organizationId && { 'x-debug-organization-id': organizationId }),
		'x-debug-site-route': window.location.href,
	};
}
